import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import * as validationConstants from './validation-constants';
import { LoadingPane, accessibilityNotificationsEmitter } from '../../common';
import './validate-intake-form.scss';

const contentLoadedMessage = (passed) => "Validation " + (passed ? "Passed" : "Failed");
const loadingMessage = "Validating Intake Form...";

export class IntakeFormValidation extends Component {

  componentWillUpdate(nextProps, nextState) {
    const { isLoading : wasLoading } = this.props;
    const { isLoading, validationResult } = nextProps;

    const hasErrors = !!validationResult && validationConstants.allSections.some(section => this.showSection(section));

    if(!!validationResult && hasErrors) {
      if(!isLoading && wasLoading) {
        accessibilityNotificationsEmitter.polite(contentLoadedMessage(!!validationResult && validationResult.passedValidation));
      }
      else if(!wasLoading && isLoading) {
        accessibilityNotificationsEmitter.polite(loadingMessage);
      }
    }
  }

  /**
   * checks to see if a section of the display should be shown
   * based on the number of errors in the section and whether or not the section is included
   * in the list of excludedSections
   * @param {string} section
   */
  showSection(section) {
    const { validationResult, excludedSections } = this.props;
    var sectionErrors = validationResult && validationResult.validationErrors && validationResult.validationErrors.filter(error => error.section === section);
    return (!!excludedSections ? !excludedSections.includes(section) : true) && !!sectionErrors && sectionErrors.length > 0;
  }

  /**
   * creates a JSX display showing all the validation errors not pertaining to Intake Questions
   * displays the following errors:
   * disabled site
   * disabled program
   */
  renderAboutSection() {
    const { validationResult, hideSectionHeaders } = this.props;
    if(!this.showSection(validationConstants.aboutSection)) {
      return null;
    }

    var aboutErrors = validationResult.validationErrors.filter(error => error.section === validationConstants.aboutSection);
    return (
      <div id="about">
        { hideSectionHeaders ? null : <h3>About</h3> }
        { aboutErrors.map(val => <div id="about-error">{ val.error }</div>)}
      </div>
    );
  }

  /**
   * creates a JSX display showing the errors pertaining to demographic questions
   * displays the following errors:
   * unsupported demographic questions that are required
   * view only demographic questions that are required
   */
  renderDemographicsSection() {
    const { validationResult, hideSectionHeaders } = this.props;
    if(!this.showSection(validationConstants.demographicsSection)) {
      return null;
    }

    var demographicErrors = validationResult.validationErrors.filter(error => error.section === validationConstants.demographicsSection);
    return (
      <div id="demographics">
        { hideSectionHeaders ? null : <h3>Demographics</h3> }
        { demographicErrors.map(val => <div id="demographic-error">{ val.error }</div>)}
      </div>
    );
  }

  /**
   * creates a JSX display showing the errors pertaining to touchpoint questions
   * displays the following errors:
   * touchpoint is disabled
   * unsupported touchpoint questions that are required
   * contains unsupported conditional rules
   */
  renderTouchpointSection() {
    const { validationResult, hideSectionHeaders } = this.props;
    var touchpointErrors = validationResult.validationErrors.filter(error => error.section === validationConstants.touchpointSection);

    var touchpointErrorDictionary = {};
    touchpointErrors.forEach(val => {
      if(touchpointErrorDictionary[val.formId]) {
        touchpointErrorDictionary[val.formId].push(val);
      }
      else {
        touchpointErrorDictionary[val.formId] = [val];
      }
    });
    var touchpointSections = [];
    for(var formId in touchpointErrorDictionary) {
      var singleFormErrors = touchpointErrorDictionary[formId];
      touchpointSections.push({
        id: formId,
        name: singleFormErrors[0].formName,
        otherErrors: singleFormErrors.filter(val => val.type !== validationConstants.touchpointUnsupported &&
                                                    val.type !== validationConstants.ruleUnsupported),
        unsupportedErrors: singleFormErrors.filter(val => val.type === validationConstants.touchpointUnsupported),
        conditionalRuleErrors: singleFormErrors.filter(val => val.type === validationConstants.ruleUnsupported),
      })
    }
    var touchpointSection = this.showSection(validationConstants.touchpointSection) ? (
      <div id="touchpoint-section">
        { touchpointSections && touchpointSections.map(touchpointValidation => (
        <div id={touchpointValidation.id}>
          { hideSectionHeaders ? null : <h3>Touchpoint "{touchpointValidation.name}"</h3> }
          <p>The TouchPoint cannot be used in an intake form:</p>
          { touchpointValidation.otherErrors.map(val => <div id={touchpointValidation.id + "-error"}>{ val.error }</div>)}
          { touchpointValidation.unsupportedErrors.length > 0 ? (
            <ul>
              It includes TouchPoint elements that may display personally identifiable information or other sensitive information:
              { touchpointValidation.unsupportedErrors.map( (val, index) => <li id={touchpointValidation.id + "-error"} key={index}>{val.typeLabel}: {val.label}</li>)}
            </ul>
          ) : null}
          { touchpointValidation.conditionalRuleErrors.map((ruleError, errorIndex) => (
            <ul id={touchpointValidation.id + "-error"} key={errorIndex}>
              { ruleError.error }
              { ruleError.reasons && ruleError.reasons.map( (reason, reasonIndex) => {
                return <li key={reasonIndex}>{reason ? <span> {reason}</span> : null}</li>
              })}
            </ul>
          ))}
        </div>
      ))}
      </div>
    ) : null;
    return touchpointSection;
  }

  /**
   * combines the displays of About, Demographic, and Touchpoint errors
   * returns null if there are no errors to display
   */
  renderValidationDisplay() {
    const { validationResult, titleMessage, isLoading } = this.props;

    if(!validationResult)
      return null;

    var aboutSection = this.renderAboutSection();

    var demographicsSection = this.renderDemographicsSection();

    var touchpointSection = this.renderTouchpointSection();

    var validationDisplay = null;
    if(!!aboutSection || !!demographicsSection || !!touchpointSection) {
      validationDisplay = (
        <div id="validation-errors" className="alert alert-danger">
          { isLoading && !!validationResult ? (
            <div className="loading-icon-wrapper">
              <span className="glyphicon glyphicon-refresh spin loading-icon"/>
            </div>
          ) : null }
          <div>{titleMessage}</div>
          { aboutSection }
          { demographicsSection }
          { touchpointSection }
        </div>
      );
    }
    return validationDisplay;
  }

  /**
   * returns a JSX display showing the Loading errors that occured when trying to perform validation
   */
  renderLoadingErrorsSection() {
    const { validationResult, loadingErrorCondition, loadingErrorInstructions } = this.props;
    if(!this.showSection(validationConstants.loadingSection)) {
      return null;
    }

    var loadingErrors = validationResult.validationErrors.filter(error => error.section === validationConstants.loadingSection);
    var subjects = "";
    loadingErrors.forEach((val, index) => {
      subjects += val.subject;

      if(index < loadingErrors.length - 1) {
        subjects += loadingErrors.length > 2 ? ', ' : ' ';
      }
      if(index === loadingErrors.length - 2) {
        subjects += 'and '
      }
    });

    var loadingErrorsSection = (
      <div id="loading-errors" className="alert alert-danger">
        <div>
          {loadingErrorCondition} {subjects} data failed to load. {loadingErrorInstructions}
        </div>
      </div>
    );

    return loadingErrorsSection;
  }

  render() {
    const { validationResult, isLoading, loadingErr } = this.props;

    var loadingDisplay = this.renderLoadingErrorsSection();
    var validationDisplay = this.renderValidationDisplay();

    const hasErrors = !!validationResult && validationConstants.allSections.some(section => this.showSection(section));

    return (
      <LoadingPane
        isLoading={isLoading && (!validationResult || !hasErrors)}
        err={loadingErr}
        loadingMessage={""}
        contentLoadedMessage={contentLoadedMessage(!!validationResult && validationResult.passedValidation)}>
        <div id="intake-validation-display">
          { loadingDisplay }
          { validationDisplay }
        </div>
      </LoadingPane>
    );
  }
}

IntakeFormValidation.propTypes = {
  loadingErrorCondition: PropTypes.string,
  loadingErrorInstructions: PropTypes.string,
  validationResult: PropTypes.object,
  isLoading: PropTypes.bool,
  loadingErr: PropTypes.object,
  excludedSections: PropTypes.array,
  titleMessage: PropTypes.string,
  hideSectionHeaders: PropTypes.bool,
};

export default IntakeFormValidation;
